import {differenceInCalendarDays, format, max, min} from 'date-fns';
import {isOutsideProjectDatesAllowed, isOutsideTaskDatesAllowed} from '../../cache/TimeRegistrationSettingsUtil';

type DateInvalidInput = {
	date: Date;
	startDate?: Date | null;
	endDate?: Date | null;
	ignoredDates?: Date[]; // Allow passing dates which should always be legal
};

function isOutsideDateRange({date, startDate, endDate, ignoredDates = []}: DateInvalidInput): boolean {
	if (ignoredDates.length > 0) {
		if (ignoredDates.some(ignoredDate => differenceInCalendarDays(date, ignoredDate) === 0)) {
			return false;
		}
	}

	if (startDate && endDate) {
		return differenceInCalendarDays(date, startDate) < 0 || differenceInCalendarDays(date, endDate) > 0;
	}

	if (startDate) {
		return differenceInCalendarDays(date, startDate) < 0;
	}

	if (endDate) {
		return differenceInCalendarDays(date, endDate) > 0;
	}

	return false;
}

function isTaskDateInvalid(dateInvalidInput: DateInvalidInput): boolean {
	if (isOutsideTaskDatesAllowed()) {
		return false;
	}

	return isOutsideDateRange(dateInvalidInput);
}

function isProjectDateInvalid(dateInvalidInput: DateInvalidInput): boolean {
	if (isOutsideProjectDatesAllowed()) {
		return false;
	}

	return isOutsideDateRange(dateInvalidInput);
}

export type TimeRegistrationDateInvalidInput = {
	date: Date;
	projectStartDate?: Date | null;
	projectEndDate?: Date | null;
	taskStartDate?: Date | null;
	taskEndDate?: Date | null;
	ignoredDates?: Date[];
};
export function isTimeRegistrationDateInvalid({
	date,
	projectStartDate,
	projectEndDate,
	taskStartDate,
	taskEndDate,
	ignoredDates,
}: TimeRegistrationDateInvalidInput): boolean {
	return (
		isTaskDateInvalid({date, startDate: taskStartDate, endDate: taskEndDate, ignoredDates}) ||
		isProjectDateInvalid({date, startDate: projectStartDate, endDate: projectEndDate, ignoredDates})
	);
}

type DateInvalidReasonStringInput = {
	entityType: string;
	startDate?: Date | null;
	endDate?: Date | null;
};
function getDateInvalidReasonString({entityType, startDate, endDate}: DateInvalidReasonStringInput) {
	const startDateString = startDate ? format(startDate, 'P') : '';
	const endDateString = endDate ? format(endDate, 'P') : '';
	return `Time registration must be within ${entityType} dates: ${startDateString} - ${endDateString}`;
}
export function isTimeRegistrationDateInvalidWithReason({
	date,
	projectStartDate,
	projectEndDate,
	taskStartDate,
	taskEndDate,
	ignoredDates,
}: TimeRegistrationDateInvalidInput): string | null {
	if (isTaskDateInvalid({date, startDate: taskStartDate, endDate: taskEndDate, ignoredDates})) {
		return getDateInvalidReasonString({entityType: 'task', startDate: taskStartDate, endDate: taskEndDate});
	}
	if (isProjectDateInvalid({date, startDate: projectStartDate, endDate: projectEndDate, ignoredDates})) {
		return getDateInvalidReasonString({entityType: 'project', startDate: projectStartDate, endDate: projectEndDate});
	}

	return null;
}

export type TimeRegistrationDateRestrictionInfoMessageInput = {
	projectStartDate?: Date | null;
	projectEndDate?: Date | null;
	taskStartDate?: Date | null;
	taskEndDate?: Date | null;
};
export function getTimeRegistrationDateRestrictionInfoMessage({
	projectStartDate,
	projectEndDate,
	taskStartDate,
	taskEndDate,
}: TimeRegistrationDateRestrictionInfoMessageInput): string | null {
	if (
		!isOutsideTaskDatesAllowed() &&
		!isOutsideProjectDatesAllowed() &&
		(taskStartDate || taskEndDate || projectStartDate || projectEndDate)
	) {
		const startDate =
			taskStartDate && projectStartDate ? max([taskStartDate, projectStartDate]) : taskStartDate || projectStartDate;
		const endDate = taskEndDate && projectEndDate ? min([taskEndDate, projectEndDate]) : taskEndDate || projectEndDate;
		return getDateInvalidReasonString({entityType: 'task and project', startDate: startDate, endDate: endDate});
	}
	if (!isOutsideTaskDatesAllowed() && (taskStartDate || taskEndDate)) {
		return getDateInvalidReasonString({entityType: 'task', startDate: taskStartDate, endDate: taskEndDate});
	}
	if (!isOutsideProjectDatesAllowed() && (projectStartDate || projectEndDate)) {
		return getDateInvalidReasonString({entityType: 'project', startDate: projectStartDate, endDate: projectEndDate});
	}

	return null;
}
